'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { 
  LoginRequest, 
  ChangePasswordRequest,
  FrontendUser 
} from '@/types/auth';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: FrontendUser) => void;
  redirectTo?: string;
}

// Best Practice Input with ReadOnly-Trick and proper autocomplete
const BestPracticeInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { 
  isNewPassword?: boolean;
}>(({ 
  className = '', 
  isNewPassword = false,
  type,
  name,
  ...props 
}, ref) => {
  const [isReadOnly, setIsReadOnly] = useState(true);
  
  // Use non-standard names to avoid autofill detection
  const safeName = isNewPassword ? `new_pwd_${Math.random().toString(36).substr(2, 5)}` : name;
  const autoCompleteValue = isNewPassword ? 'new-password' : 'off';
  
  return (
    <input
      {...props}
      ref={ref}
      type={type}
      name={safeName}
      readOnly={isReadOnly}
      onFocus={() => setIsReadOnly(false)}
      onBlur={() => {
        // Keep readonly disabled after first interaction
        // setIsReadOnly(true);
      }}
      className={`
        block w-full px-3 py-2 
        border-2 border-gray-300 rounded-md shadow-sm 
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
        bg-white text-gray-900 font-medium
        placeholder-gray-500 text-sm
        ${className}
      `}
      style={{
        backgroundColor: '#ffffff !important',
        color: '#111827 !important',
        borderColor: '#d1d5db',
        fontSize: '14px',
        fontWeight: '500',
        WebkitBoxShadow: '0 0 0 1000px white inset !important',
        WebkitTextFillColor: '#111827 !important'
      }}
      autoComplete={autoCompleteValue}
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      data-form-type="other"
      data-lpignore="true"
      data-1p-ignore="true"
    />
  );
});

BestPracticeInput.displayName = 'BestPracticeInput';

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  redirectTo
}) => {
  const { login, changePassword, isLoading } = useAuth();
  
  const [mode, setMode] = useState<'login' | 'change-password'>('login');
  const [currentUser, setCurrentUser] = useState<FrontendUser | null>(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember_me: false
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (mode === 'login') {
          usernameRef.current?.focus();
        } else {
          newPasswordRef.current?.focus();
        }
      }, 200);
      
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, mode]);
  
  const handleClose = () => {
    setError('');
    setSuccess('');
    setFormData({ username: '', password: '', remember_me: false });
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setMode('login');
    setCurrentUser(null);
    onClose();
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    console.log('🔤 Login Input change:', name, '=', type === 'password' ? '[HIDDEN]' : value);
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Extract the real field name from our safe names
    let fieldName = '';
    if (e.target.name?.includes('new_pwd')) {
      fieldName = 'new_password';
    } else if (e.target.name === 'current_pwd') {
      fieldName = 'current_password';
    } else if (e.target.name === 'confirm_pwd') {
      fieldName = 'confirm_password';
    }
    
    console.log('🔐 Password change field:', fieldName, '= [', value.length, 'chars]');
    setPasswordData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setError('');
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    
    console.log('🚀 LOGIN FORM SUBMIT:', {
      username: formData.username,
      passwordLength: formData.password.length,
      remember: formData.remember_me
    });
    
    try {
      const credentials: LoginRequest = {
        username: formData.username.trim(),
        password: formData.password,
        remember_me: formData.remember_me
      };
      
      const result = await login(credentials);
      
      if (result.success && result.user) {
        if (result.must_change_password) {
          console.log('🔄 User must change password, switching to change mode');
          setCurrentUser(result.user);
          setMode('change-password');
          setSuccess('Bitte ändern Sie Ihr Passwort bei der ersten Anmeldung.');
        } else {
          setSuccess('Anmeldung erfolgreich!');
          onLoginSuccess(result.user);
          
          setTimeout(() => {
            handleClose();
          }, 1000);
        }
      } else {
        setError(result.message || 'Anmeldung fehlgeschlagen');
        
        if (result.account_locked) {
          const lockExpires = result.lock_expires_at ? 
            new Date(result.lock_expires_at).toLocaleString('de-DE') : 'unbekannt';
          setError(`Konto ist gesperrt bis ${lockExpires}`);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Netzwerkfehler bei der Anmeldung');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    
    console.log('🔐 PASSWORD CHANGE FORM SUBMIT:', {
      currentPasswordLength: passwordData.current_password.length,
      newPasswordLength: passwordData.new_password.length,
      confirmPasswordLength: passwordData.confirm_password.length,
      forceChange: currentUser?.must_change_password
    });
    
    try {
      if (passwordData.new_password !== passwordData.confirm_password) {
        setError('Passwort-Bestätigung stimmt nicht überein');
        setIsSubmitting(false);
        return;
      }
      
      if (passwordData.new_password.length < 8) {
        setError('Passwort muss mindestens 8 Zeichen lang sein');
        setIsSubmitting(false);
        return;
      }
      
      const changeRequest: ChangePasswordRequest = {
        current_password: passwordData.current_password || undefined,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password,
        force_change: currentUser?.must_change_password || false
      };
      
      console.log('🔄 CALLING CHANGE PASSWORD API...');
      const result = await changePassword(changeRequest);
      
      if (result.success) {
        setSuccess('Passwort erfolgreich geändert!');
        
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            must_change_password: false
          };
          
          onLoginSuccess(updatedUser);
          
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      } else {
        setError(result.message || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setError('Netzwerkfehler beim Passwort ändern');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 transform transition-all">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'login' ? 'InspecDoor Anmeldung' : 'Passwort ändern'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}
        
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="username" className="text-gray-700 font-medium">Benutzername</Label>
              <BestPracticeInput
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="mt-1"
                placeholder="Benutzername eingeben"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">Passwort</Label>
              <BestPracticeInput
                ref={passwordRef}
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="mt-1"
                placeholder="Passwort eingeben"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                id="remember_me"
                name="remember_me"
                type="checkbox"
                checked={formData.remember_me}
                onChange={handleInputChange}
                disabled={isSubmitting}
                className="rounded border-gray-300"
              />
              <Label htmlFor="remember_me" className="text-sm text-gray-700">
                Angemeldet bleiben
              </Label>
            </div>
            
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
            >
              {isSubmitting ? 'Anmelden...' : 'Anmelden'}
            </Button>
          </form>
        )}
        
        {mode === 'change-password' && (
          <div>
            {/* Anti-autofill dummy fields as recommended by best practices */}
            <div style={{ display: 'none' }}>
              <input type="text" autoComplete="username" />
              <input type="password" autoComplete="current-password" />
            </div>
            
            <form onSubmit={handlePasswordChangeSubmit} className="space-y-4" autoComplete="off">
              {currentUser && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Willkommen <strong>{currentUser.full_name}</strong>! 
                    Bitte ändern Sie Ihr Passwort bei der ersten Anmeldung.
                  </p>
                </div>
              )}
              
              {!currentUser?.must_change_password && (
                <div>
                  <Label htmlFor="current_pwd" className="text-gray-700 font-medium">Aktuelles Passwort</Label>
                  <BestPracticeInput
                    id="current_pwd"
                    name="current_pwd"
                    type="password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required={!currentUser?.must_change_password}
                    disabled={isSubmitting}
                    className="mt-1"
                    placeholder="Aktuelles Passwort eingeben"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="new_pwd" className="text-gray-700 font-medium">
                  Neues Passwort <span className="text-xs text-green-600">(sichtbar eingeben)</span>
                </Label>
                <BestPracticeInput
                  ref={newPasswordRef}
                  id="new_pwd"
                  name="new_pwd"
                  type="text"
                  isNewPassword={true}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  disabled={isSubmitting}
                  className="mt-1"
                  placeholder="Neues Passwort (min. 8 Zeichen)"
                />
              </div>
              
              <div>
                <Label htmlFor="confirm_pwd" className="text-gray-700 font-medium">
                  Passwort bestätigen <span className="text-xs text-green-600">(sichtbar)</span>
                </Label>
                <BestPracticeInput
                  id="confirm_pwd"
                  name="confirm_pwd"
                  type="text"
                  isNewPassword={true}
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  disabled={isSubmitting}
                  className="mt-1"
                  placeholder="Neues Passwort wiederholen"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md"
              >
                {isSubmitting ? 'Passwort ändern...' : 'Passwort ändern'}
              </Button>
              
              {!currentUser?.must_change_password && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('login')}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  Zurück zur Anmeldung
                </Button>
                )}
            </form>
          </div>
        )}
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            InspecDoor © - Metallbau Steffens<br />
            Sichere Tür-Prüfungsdokumentation
          </p>
        </div>
      </div>
    </div>
  );
};
